import { Fragment, useCallback, useRef, useState, ChangeEvent } from "react";
import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  Portal,
  Transition,
} from "@headlessui/react";
import {
  cx,
  useSearchInput,
  useTranslations,
  useIsOverflowing,
} from "@jugl-web/utils";
import {
  InventoryItem,
  InvoiceLineItem,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { LoadingSpinner, Tooltip } from "@jugl-web/ui-kit";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useInvoicesItemFormPageContext } from "@web-src/modules/invoices/pages/InvoicesItemFormPage/context/useInvoicesItemFormPageContext";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { ReactComponent as NoImageIndicatorIcon } from "./assets/no-image-indicator.svg";
import { ItemsFormAddServiceDialog } from "../../../ItemsFormAddServiceDialog";
import { ReactComponent as ServiceIcon } from "./assets/service.svg";
import { ReactComponent as PlusIcon } from "./assets/plus.svg";

interface ComponentProps {
  idx: number;
  tableItem: InvoiceLineItem;
}

export const NameCell = ({ tableItem, idx }: ComponentProps) => {
  const { t } = useTranslations();
  const { entity } = useEntitySelectedProvider();
  const { addInventoryItem } = useInvoicesItemFormPageContext();
  const { inventoryApi } = useRestApiProvider();
  const { data: inventoryItem } = inventoryApi.useGetInventoryItemQuery(
    tableItem?.inventory_id
      ? {
          itemId: tableItem.inventory_id,
          entityId: entity.id,
          meta: { silent: true },
        }
      : skipToken
  );
  const { currencySymbol, customErrors, setCustomErrors, itemsField } =
    useInvoicesItemFormPageContext();
  const isLastItem = idx === itemsField.fields.length - 1;

  const [isAddServiceModalOpen, setIsAddServiceModalOpen] = useState(false);

  const [status, setStatus] = useState<
    "no-results" | "is-fetching" | "loaded" | "error"
  >("is-fetching");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { isOverflowing, checkOverflow } = useIsOverflowing(inputRef);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const blockAddCustomItem = useRef(false);

  const getMainImage = (images: { path: string; order: number | null }[]) =>
    images.find(({ order }) => order === 1)?.path || images[0].path;

  const updatePosition = () => {
    if (inputRef.current) {
      const input = inputRef.current;
      const rect = input.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY - 10,
        left: rect.left + window.scrollX,
      });
    }
  };

  const { searchQuery, inputProps, reset } = useSearchInput({
    debounce: 500,
    onSearch: (value) => {
      if (value) {
        loadMoreInventoryItems(true, false, value);
      }
      if (value === "" || !value) {
        loadMoreInventoryItems(true, true, undefined);
      }
    },
  });
  const setSearchQuery = useCallback(
    (query: string) => {
      inputProps.onChange({
        target: { value: query },
      } as unknown as ChangeEvent<HTMLInputElement>);
    },
    [inputProps]
  );

  const [resultItems, setResultItems] = useState<InventoryItem[]>([]);
  const [loadMoreItems, { isLoading }] =
    inventoryApi.useLazyGetInventoryItemsListQuery();

  const loadMoreInventoryItems = useCallback(
    async (
      shouldReset?: boolean,
      clearSearchValue?: boolean,
      searchValue?: string
    ) => {
      if (!shouldReset && (isLoading || !hasMore || status === "is-fetching")) {
        return;
      }
      setStatus("is-fetching");

      if (shouldReset) {
        setPage(1);
        setResultItems([]);
        setHasMore(true);
      }

      const response = await loadMoreItems({
        params: {
          page: shouldReset ? 1 : page + 1,
          page_size: 10,
          search: clearSearchValue
            ? undefined
            : searchValue || searchQuery || undefined,
        },
        entityId: entity.id,
      });

      if (response?.data?.data) {
        const { data } = response.data;

        if (data.length === 0) {
          setStatus("no-results");
        } else {
          setStatus("loaded");
        }

        if (response.data.page_number === response.data.total_pages) {
          setHasMore(false);
        }
        setResultItems((prev) => {
          const uniqueItems = new Map(
            [...prev, ...data].map((item) => [item.id, item])
          );
          return Array.from(uniqueItems.values());
        });
        setPage(response.data.page_number);
      } else {
        setStatus("error");
      }
    },
    [isLoading, hasMore, status, loadMoreItems, page, searchQuery, entity]
  );

  const handleSelect = (item: InventoryItem | null) => {
    if (!item) return;
    blockAddCustomItem.current = true;
    addInventoryItem(item, 1, idx);
    reset();
    inputProps.onChange({
      target: { value: item.name },
    } as unknown as ChangeEvent<HTMLInputElement>);
  };

  const handleBlur = () => {
    if (blockAddCustomItem.current) {
      blockAddCustomItem.current = false;
    }
    const trimmedValue = tableItem.name.trim();
    if (tableItem.name !== trimmedValue && trimmedValue) {
      itemsField.update(idx, { ...tableItem, name: trimmedValue });
    }
    if (!isLastItem && !trimmedValue) {
      itemsField.remove(idx);
    }
  };

  const hasEmptyListError = !!customErrors.items;
  const imagePath = inventoryItem?.img?.[0]?.path;

  return (
    <>
      <div
        className={cx("flex h-full w-full items-center gap-3 pl-4", {
          "border border-solid border-red-500": hasEmptyListError,
        })}
      >
        {imagePath ? (
          <img
            src={imagePath}
            alt={tableItem.name}
            className="absolute h-8 w-8 rounded-lg object-cover"
          />
        ) : null}
        <div
          className={cx("relative h-full w-full", {
            "pl-9": inventoryItem?.img?.[0]?.path,
          })}
        >
          <Combobox onChange={handleSelect}>
            <Tooltip
              delay="none"
              placement="right"
              isDisabled={!isOverflowing}
              renderTrigger={({ props, ref, open }) => (
                <div ref={ref} {...props} className="h-full">
                  <ComboboxInput
                    ref={inputRef}
                    className="h-full w-full cursor-text truncate rounded border-none bg-transparent px-1 text-base font-medium focus:outline-none"
                    value={tableItem.name}
                    placeholder={t({
                      id: "invoices.item.name.placeholder",
                      defaultMessage: "Enter Item/Service Name",
                    })}
                    onChange={(event) => {
                      setSearchQuery(event.target.value);
                      updatePosition();
                      itemsField.update(idx, {
                        ...tableItem,
                        name: event.target.value,
                      });
                      const errorsCopy = { ...customErrors };
                      delete errorsCopy.items;
                      setCustomErrors(errorsCopy);
                    }}
                    onFocus={(e) => {
                      e.target.focus();
                      updatePosition();
                    }}
                    onMouseEnter={() => {
                      const isOverflow = checkOverflow();

                      if (isOverflow) {
                        open();
                      }
                    }}
                    onBlur={handleBlur}
                  />
                </div>
              )}
            >
              {tableItem.name}
            </Tooltip>
            <Portal>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                afterLeave={() => reset()}
              >
                <ComboboxOptions
                  className="fixed z-[100] mt-3 ml-[-8px] max-h-60 w-[500px] overflow-auto rounded-lg bg-white p-2 shadow-lg"
                  style={{
                    top: `${position.top}px`,
                    left: `${position.left - (imagePath ? 44 : 0)}px`,
                  }}
                >
                  {status === "is-fetching" ? (
                    <div className="flex h-14 items-center justify-center">
                      <LoadingSpinner />
                    </div>
                  ) : status === "loaded" ? (
                    resultItems.map((item, resultIdx) => (
                      <Combobox.Option
                        key={+resultIdx}
                        value={item}
                        className={({ active }) =>
                          `flex cursor-pointer items-center justify-between gap-3 px-4 py-3 font-secondary font-normal hover:bg-grey-100 ${
                            active ? "bg-grey-100" : ""
                          }`
                        }
                      >
                        <div className="flex items-center gap-3">
                          {item.img.length > 0 ? (
                            <img
                              src={getMainImage(item.img)}
                              alt={item.name}
                              className="h-9 w-9 rounded-full"
                            />
                          ) : (
                            <div className="flex h-8 w-8 items-center justify-center rounded-[10px] bg-grey-200">
                              <NoImageIndicatorIcon />
                            </div>
                          )}

                          <div className="flex flex-col">
                            <span className="max-w-[320px] truncate text-base text-dark-800">
                              {item.name}
                            </span>
                            <span className="text-sm text-[#828282] capitalize">
                              {item.category}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-right text-sm font-medium">
                          <span className="font-primary text-[#828282]">
                            {currencySymbol}
                          </span>
                          <span className="text-dark-800">{item.price}</span>
                        </div>
                      </Combobox.Option>
                    ))
                  ) : status === "no-results" ? (
                    <div>
                      <div className="px-4 py-3 text-center text-grey-600">
                        {t({
                          id: "invoices.no-results",
                          defaultMessage: "No results 😔",
                        })}
                      </div>
                    </div>
                  ) : status === "error" ? (
                    <div className="px-4 py-3 text-center text-grey-600">
                      {t({
                        id: "invoices.error",
                        defaultMessage: "Error",
                      })}
                    </div>
                  ) : null}

                  {["loaded", "no-results"].includes(status) && (
                    <div
                      className="mt-6 flex h-10 w-full shrink-0 cursor-pointer items-center justify-between rounded-lg border-none bg-grey-100 px-3 transition-colors hover:bg-grey-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsAddServiceModalOpen(true);
                        blockAddCustomItem.current = true;
                      }}
                    >
                      <div className="flex items-center gap-2.5">
                        <ServiceIcon />

                        <span className="font-[Roboto] text-sm text-dark">
                          {t({
                            id: "invoices.add-item-service",
                            defaultMessage: "Add Item/Service",
                          })}
                        </span>
                      </div>
                      <PlusIcon />
                    </div>
                  )}
                </ComboboxOptions>
              </Transition>
            </Portal>
          </Combobox>
        </div>
      </div>
      <div className="z-[100]">
        <ItemsFormAddServiceDialog
          isOpen={isAddServiceModalOpen}
          onClose={() => setIsAddServiceModalOpen(false)}
          onSuccess={({ item, quantity, isCustom }) => {
            if (isCustom) {
              itemsField.update(idx, {
                ...tableItem,
                name: item.name,
                desc: item.description,
                unit: item.unit || null,
                category: item.category || "item",
                qty: parseInt(quantity as unknown as string, 10),
                price: parseFloat(item.price),
              });
            } else {
              addInventoryItem(item as InventoryItem, 1, idx);
            }
            setIsAddServiceModalOpen(false);
          }}
        />
      </div>
    </>
  );
};
